import React, { useEffect, useState, useRef, useMemo } from 'react';
import { gitGameStateService, GameStateBranch, PlayerChoiceCommit } from '../services/gitGameStateService';
import { karmaService } from '../services/karmaService';
import * as d3 from 'd3';
import { ClockIcon, ArrowPathIcon, SparklesIcon } from '@heroicons/react/24/outline';

interface TeleportNode {
  id: string;
  commitHash: string;
  branchId: string;
  timestamp: string;
  karma: number;
  choiceText: string;
  consequences: string;
  playerName: string;
  canTeleport: boolean;
  teleportType: 'past' | 'future' | 'parallel';
  x?: number;
  y?: number;
  children: TeleportNode[];
  genre?: string;
  worldKarmaAtTime?: number;
}

interface TemporalPath {
  from: TeleportNode;
  to: TeleportNode;
  pathType: 'linear' | 'merge' | 'branch' | 'temporal';
  karmaFlow: number;
}

interface Props {
  cartridgeId: string;
  userId: string;
  currentBranchId: string;
  playerKarma: number;
  onTeleport: (nodeId: string, targetTime: string) => Promise<void>;
  onMergeSelect: (branchId: string) => void;
  isVisible: boolean;
}

export const InteractiveGitTreeVisualizer: React.FC<Props> = ({
  cartridgeId,
  userId,
  currentBranchId,
  playerKarma,
  onTeleport,
  onMergeSelect,
  isVisible
}) => {
  const [teleportTree, setTeleportTree] = useState<TeleportNode | null>(null);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [temporalPaths, setTemporalPaths] = useState<TemporalPath[]>([]);
  const [loading, setLoading] = useState(false);
  const [showTeleportModal, setShowTeleportModal] = useState(false);
  const [teleportTarget, setTeleportTarget] = useState<TeleportNode | null>(null);
  const [karmaThreshold, setKarmaThreshold] = useState(10);
  const [timeFilter, setTimeFilter] = useState<'all' | 'past' | 'future'>('all');
  
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const dimensions = useMemo(() => ({
    width: 1200,
    height: 800,
    margin: { top: 50, right: 50, bottom: 50, left: 50 }
  }), []);

  useEffect(() => {
    if (isVisible) {
      loadGitTreeData();
    }
  }, [cartridgeId, currentBranchId, isVisible]);

  useEffect(() => {
    if (teleportTree && isVisible) {
      renderGitTree();
    }
  }, [teleportTree, selectedNode, hoveredNode, karmaThreshold, timeFilter, isVisible]);

  const loadGitTreeData = async () => {
    try {
      setLoading(true);
      
      // Get all branches for this cartridge
      const branches = await gitGameStateService.getCartridgeBranches(cartridgeId);
      const currentBranch = branches.find(b => b.id === currentBranchId);
      
      if (!currentBranch) return;
      
      // Build teleport tree from commits
      const tree = await buildTeleportTree(branches, currentBranch);
      setTeleportTree(tree);
      
      // Calculate temporal paths
      const paths = calculateTemporalPaths(tree);
      setTemporalPaths(paths);
      
    } catch (error) {
      console.error('[GitTree] Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildTeleportTree = async (branches: GameStateBranch[], currentBranch: GameStateBranch): Promise<TeleportNode> => {
    const allCommits: PlayerChoiceCommit[] = [];
    const branchMap = new Map<string, GameStateBranch>();
    
    // Collect all commits from all branches
    branches.forEach(branch => {
      branchMap.set(branch.id, branch);
      if (branch.playerChoices) {
        allCommits.push(...branch.playerChoices.map(commit => ({
          ...commit,
          branchId: branch.id
        })));
      }
    });
    
    // Sort by timestamp to build temporal relationships
    allCommits.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    
    const nodeMap = new Map<string, TeleportNode>();
    const rootNode: TeleportNode = {
      id: 'root',
      commitHash: 'genesis',
      branchId: currentBranchId,
      timestamp: new Date().toISOString(),
      karma: 0,
      choiceText: 'Story Genesis',
      consequences: 'The beginning of all possible paths',
      playerName: 'System',
      canTeleport: false,
      teleportType: 'past',
      children: [],
      genre: 'cyberpunk',
      worldKarmaAtTime: 0
    };
    
    nodeMap.set('root', rootNode);
    
    // Build nodes from commits
    allCommits.forEach((commit, index) => {
      const currentTime = new Date();
      const commitTime = new Date(commit.timestamp);
      const isInPast = commitTime < currentTime;
      const isInFuture = commitTime > currentTime;
      
      // Determine if player can teleport to this node
      const karmaDiff = Math.abs(playerKarma - (commit.choiceData?.karmaImpact || 0));
      const canTeleport = karmaDiff <= karmaThreshold;
      
      const node: TeleportNode = {
        id: commit.hash,
        commitHash: commit.hash,
        branchId: commit.branchId,
        timestamp: commit.timestamp,
        karma: commit.choiceData?.karmaImpact || commit.karmaDelta || 0,
        choiceText: commit.choiceData?.choiceText || commit.choice?.choiceText || 'Unknown choice',
        consequences: commit.choiceData?.consequences || commit.choice?.consequences || 'Unknown consequences',
        playerName: commit.playerName,
        canTeleport,
        teleportType: isInPast ? 'past' : isInFuture ? 'future' : 'parallel',
        children: [],
        genre: branchMap.get(commit.branchId)?.genre || 'cyberpunk',
        worldKarmaAtTime: calculateWorldKarmaAtTime(allCommits, index)
      };
      
      nodeMap.set(commit.hash, node);
    });
    
    // Build parent-child relationships
    allCommits.forEach(commit => {
      const node = nodeMap.get(commit.hash);
      if (!node) return;
      
      if (commit.parentCommitId || commit.parentCommit) {
        const parentId = commit.parentCommitId || commit.parentCommit;
        const parent = nodeMap.get(parentId) || rootNode;
        parent.children.push(node);
      } else {
        rootNode.children.push(node);
      }
    });
    
    return rootNode;
  };

  const calculateWorldKarmaAtTime = (commits: PlayerChoiceCommit[], upToIndex: number): number => {
    return commits.slice(0, upToIndex + 1)
      .reduce((sum, commit) => sum + (commit.choiceData?.karmaImpact || commit.karmaDelta || 0), 0);
  };

  const calculateTemporalPaths = (rootNode: TeleportNode): TemporalPath[] => {
    const paths: TemporalPath[] = [];
    
    const traverse = (node: TeleportNode) => {
      node.children.forEach(child => {
        const karmaFlow = child.karma - node.karma;
        
        let pathType: TemporalPath['pathType'] = 'linear';
        if (node.branchId !== child.branchId) {
          pathType = 'merge';
        } else if (node.children.length > 1) {
          pathType = 'branch';
        } else if (Math.abs(karmaFlow) > 20) {
          pathType = 'temporal';
        }
        
        paths.push({
          from: node,
          to: child,
          pathType,
          karmaFlow
        });
        
        traverse(child);
      });
    };
    
    traverse(rootNode);
    return paths;
  };

  const renderGitTree = () => {
    if (!teleportTree || !svgRef.current) return;
    
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    
    const { width, height, margin } = dimensions;
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;
    
    // Create main group
    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);
    
    // Create tree layout
    const treeLayout = d3.tree<TeleportNode>()
      .size([innerWidth, innerHeight])
      .separation((a, b) => a.parent === b.parent ? 1.5 : 2);
    
    // Convert to hierarchy and layout
    const root = d3.hierarchy(teleportTree);
    treeLayout(root);
    
    // Filter nodes based on time filter
    const filteredNodes = root.descendants().filter(d => {
      if (timeFilter === 'all') return true;
      return d.data.teleportType === timeFilter;
    });
    
    // Create gradient definitions for different node types
    const defs = svg.append('defs');
    
    // Past node gradient (blue tones)
    const pastGradient = defs.append('linearGradient')
      .attr('id', 'pastGradient')
      .attr('gradientUnits', 'objectBoundingBox');
    pastGradient.append('stop').attr('offset', '0%').attr('stop-color', '#3b82f6');
    pastGradient.append('stop').attr('offset', '100%').attr('stop-color', '#1e40af');
    
    // Future node gradient (purple tones)
    const futureGradient = defs.append('linearGradient')
      .attr('id', 'futureGradient')
      .attr('gradientUnits', 'objectBoundingBox');
    futureGradient.append('stop').attr('offset', '0%').attr('stop-color', '#a855f7');
    futureGradient.append('stop').attr('offset', '100%').attr('stop-color', '#7c3aed');
    
    // Present/parallel node gradient (cyan tones)
    const parallelGradient = defs.append('linearGradient')
      .attr('id', 'parallelGradient')
      .attr('gradientUnits', 'objectBoundingBox');
    parallelGradient.append('stop').attr('offset', '0%').attr('stop-color', '#06b6d4');
    parallelGradient.append('stop').attr('offset', '100%').attr('stop-color', '#0891b2');
    
    // Teleportable node glow filter
    const glowFilter = defs.append('filter')
      .attr('id', 'glow')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');
    
    const feGaussianBlur = glowFilter.append('feGaussianBlur')
      .attr('stdDeviation', '3')
      .attr('result', 'coloredBlur');
    
    const feMerge = glowFilter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');
    
    // Draw temporal paths
    temporalPaths.forEach(path => {
      const sourceNode = filteredNodes.find(n => n.data.id === path.from.id);
      const targetNode = filteredNodes.find(n => n.data.id === path.to.id);
      
      if (!sourceNode || !targetNode) return;
      
      const pathColor = getPathColor(path.pathType, path.karmaFlow);
      const pathWidth = Math.min(Math.abs(path.karmaFlow) / 10 + 1, 5);
      
      g.append('path')
        .attr('d', createCurvedPath(sourceNode, targetNode))
        .attr('fill', 'none')
        .attr('stroke', pathColor)
        .attr('stroke-width', pathWidth)
        .attr('opacity', 0.6)
        .attr('class', `path-${path.pathType}`)
        .style('filter', path.pathType === 'temporal' ? 'drop-shadow(0 0 5px currentColor)' : 'none');
    });
    
    // Draw teleport nodes
    const nodeGroups = g.selectAll('.teleport-node')
      .data(filteredNodes)
      .enter()
      .append('g')
      .attr('class', 'teleport-node')
      .attr('transform', d => `translate(${d.x},${d.y})`)
      .style('cursor', d => d.data.canTeleport ? 'pointer' : 'default')
      .on('click', (event, d) => {
        if (d.data.canTeleport) {
          handleTeleportClick(d.data);
        } else {
          setSelectedNode(d.data.id);
        }
      })
      .on('mouseenter', (event, d) => {
        setHoveredNode(d.data.id);
      })
      .on('mouseleave', () => {
        setHoveredNode(null);
      });
    
    // Draw node circles
    nodeGroups.append('circle')
      .attr('r', d => d.data.canTeleport ? 12 : 8)
      .attr('fill', d => getNodeFill(d.data))
      .attr('stroke', d => getNodeStroke(d.data))
      .attr('stroke-width', d => {
        if (d.data.id === selectedNode) return 3;
        if (d.data.id === hoveredNode) return 2;
        return 1;
      })
      .style('filter', d => d.data.canTeleport ? 'url(#glow)' : 'none');
    
    // Add teleport icons for teleportable nodes
    nodeGroups.filter(d => d.data.canTeleport)
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.3em')
      .attr('font-size', '10px')
      .attr('fill', 'white')
      .text(d => getTeleportIcon(d.data.teleportType));
    
    // Add karma indicators
    nodeGroups.append('text')
      .attr('x', 15)
      .attr('y', -5)
      .attr('font-size', '10px')
      .attr('fill', d => getKarmaColor(d.data.karma))
      .text(d => d.data.karma > 0 ? `+${d.data.karma}` : `${d.data.karma}`);
    
    // Add player name labels
    nodeGroups.append('text')
      .attr('x', 15)
      .attr('y', 8)
      .attr('font-size', '9px')
      .attr('fill', '#94a3b8')
      .text(d => d.data.playerName.substring(0, 8));
    
    // Add temporal distortion effects for high-karma nodes
    nodeGroups.filter(d => Math.abs(d.data.karma) > 30)
      .append('circle')
      .attr('r', 20)
      .attr('fill', 'none')
      .attr('stroke', d => d.data.karma > 0 ? '#10b981' : '#ef4444')
      .attr('stroke-width', 1)
      .attr('opacity', 0.3)
      .style('animation', 'pulse 2s infinite');
  };

  const createCurvedPath = (source: any, target: any) => {
    const dx = target.x - source.x;
    const dy = target.y - source.y;
    const dr = Math.sqrt(dx * dx + dy * dy) * 0.3;
    
    return `M${source.x},${source.y}A${dr},${dr} 0 0,1 ${target.x},${target.y}`;
  };

  const getNodeFill = (node: TeleportNode): string => {
    if (node.teleportType === 'past') return 'url(#pastGradient)';
    if (node.teleportType === 'future') return 'url(#futureGradient)';
    return 'url(#parallelGradient)';
  };

  const getNodeStroke = (node: TeleportNode): string => {
    if (node.canTeleport) return '#fbbf24';
    if (Math.abs(node.karma) > 20) return node.karma > 0 ? '#10b981' : '#ef4444';
    return '#64748b';
  };

  const getPathColor = (pathType: string, karmaFlow: number): string => {
    switch (pathType) {
      case 'temporal': return '#a855f7';
      case 'merge': return '#06b6d4';
      case 'branch': return '#f59e0b';
      default: 
        if (karmaFlow > 10) return '#10b981';
        if (karmaFlow < -10) return '#ef4444';
        return '#64748b';
    }
  };

  const getTeleportIcon = (type: string): string => {
    switch (type) {
      case 'past': return '⏪';
      case 'future': return '⏩';
      case 'parallel': return '⚡';
      default: return '●';
    }
  };

  const getKarmaColor = (karma: number): string => {
    if (karma > 20) return '#10b981';
    if (karma > 0) return '#06b6d4';
    if (karma > -20) return '#f59e0b';
    return '#ef4444';
  };

  const handleTeleportClick = (node: TeleportNode) => {
    setTeleportTarget(node);
    setShowTeleportModal(true);
  };

  const confirmTeleport = async () => {
    if (!teleportTarget) return;
    
    try {
      await onTeleport(teleportTarget.id, teleportTarget.timestamp);
      setShowTeleportModal(false);
      setTeleportTarget(null);
    } catch (error) {
      console.error('[GitTree] Teleport failed:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
        <span className="ml-3 text-purple-400">Loading temporal map...</span>
      </div>
    );
  }

  return (
    <div className="bg-gray-900/50 backdrop-blur-md rounded-lg border border-purple-500/30" ref={containerRef}>
      {/* Controls */}
      <div className="p-4 border-b border-gray-700/50">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-purple-300 flex items-center">
            <ClockIcon className="w-5 h-5 mr-2" />
            Temporal Navigation Tree
          </h3>
          
          <div className="flex items-center space-x-4">
            {/* Karma Threshold Slider */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-400">Teleport Range:</span>
              <input
                type="range"
                min="5"
                max="50"
                value={karmaThreshold}
                onChange={(e) => setKarmaThreshold(Number(e.target.value))}
                className="w-20 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
              />
              <span className="text-sm text-cyan-400">±{karmaThreshold}</span>
            </div>
            
            {/* Time Filter */}
            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value as any)}
              className="px-3 py-1 bg-gray-800 border border-gray-600 rounded-md text-sm text-white"
            >
              <option value="all">All Time</option>
              <option value="past">Past Only</option>
              <option value="future">Future Only</option>
            </select>
            
            {/* Refresh Button */}
            <button
              onClick={loadGitTreeData}
              className="p-2 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500 rounded-md transition-all"
            >
              <ArrowPathIcon className="w-4 h-4 text-purple-400" />
            </button>
          </div>
        </div>
        
        {/* Legend */}
        <div className="mt-3 flex items-center space-x-6 text-xs">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-blue-700"></div>
            <span className="text-gray-400">Past ⏪</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-gradient-to-r from-purple-500 to-purple-700"></div>
            <span className="text-gray-400">Future ⏩</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-gradient-to-r from-cyan-500 to-cyan-700"></div>
            <span className="text-gray-400">Parallel ⚡</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-yellow-400" style={{ filter: 'drop-shadow(0 0 5px currentColor)' }}></div>
            <span className="text-gray-400">Teleportable</span>
          </div>
        </div>
      </div>
      
      {/* SVG Visualization */}
      <div className="p-4">
        <svg
          ref={svgRef}
          width={dimensions.width}
          height={dimensions.height}
          className="w-full h-auto border border-gray-700/30 rounded-lg bg-gray-900/30"
        />
      </div>
      
      {/* Node Details Panel */}
      {(selectedNode || hoveredNode) && (
        <div className="p-4 border-t border-gray-700/50">
          {renderNodeDetails(selectedNode || hoveredNode)}
        </div>
      )}
      
      {/* Teleport Confirmation Modal */}
      {showTeleportModal && teleportTarget && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-lg p-6 max-w-md w-full border border-purple-500/50">
            <div className="flex items-center mb-4">
              <SparklesIcon className="w-6 h-6 text-purple-400 mr-2" />
              <h2 className="text-xl font-bold text-purple-400">Temporal Teleport</h2>
            </div>
            
            <div className="space-y-3 mb-6">
              <p className="text-gray-300">
                You are about to teleport to a different point in the story timeline:
              </p>
              
              <div className="bg-black/50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">Choice:</span>
                  <span className="text-cyan-400">{teleportTarget.choiceText}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Karma Impact:</span>
                  <span className={`${getKarmaColor(teleportTarget.karma)}`}>
                    {teleportTarget.karma > 0 ? '+' : ''}{teleportTarget.karma}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Time:</span>
                  <span className="text-gray-300">
                    {getTeleportIcon(teleportTarget.teleportType)} {teleportTarget.teleportType}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Player:</span>
                  <span className="text-gray-300">{teleportTarget.playerName}</span>
                </div>
              </div>
              
              <p className="text-sm text-yellow-400">
                ⚠️ This action will alter your current timeline and may affect your story progression.
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={confirmTeleport}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-all font-semibold"
              >
                Initiate Teleport
              </button>
              <button
                onClick={() => setShowTeleportModal(false)}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          background: #a855f7;
          cursor: pointer;
          border-radius: 50%;
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.1); }
        }
      `}</style>
    </div>
  );

  function renderNodeDetails(nodeId: string | null) {
    if (!nodeId || !teleportTree) return null;
    
    const findNode = (node: TeleportNode): TeleportNode | null => {
      if (node.id === nodeId) return node;
      for (const child of node.children) {
        const found = findNode(child);
        if (found) return found;
      }
      return null;
    };
    
    const node = findNode(teleportTree);
    if (!node) return null;
    
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-semibold text-purple-300">
            {getTeleportIcon(node.teleportType)} {node.choiceText}
          </h4>
          {node.canTeleport && (
            <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
              Teleport Available
            </span>
          )}
        </div>
        
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-400">Player:</span>
            <span className="ml-2 text-white">{node.playerName}</span>
          </div>
          <div>
            <span className="text-gray-400">Karma:</span>
            <span className={`ml-2 font-semibold ${getKarmaColor(node.karma)}`}>
              {node.karma > 0 ? '+' : ''}{node.karma}
            </span>
          </div>
          <div>
            <span className="text-gray-400">Genre:</span>
            <span className="ml-2 text-cyan-400 capitalize">{node.genre}</span>
          </div>
          <div>
            <span className="text-gray-400">World Karma:</span>
            <span className={`ml-2 ${getKarmaColor(node.worldKarmaAtTime || 0)}`}>
              {(node.worldKarmaAtTime || 0).toFixed(1)}
            </span>
          </div>
        </div>
        
        <div>
          <span className="text-gray-400 block mb-1">Consequences:</span>
          <p className="text-gray-300 text-sm italic">{node.consequences}</p>
        </div>
        
        <div className="text-xs text-gray-500">
          Commit: {node.commitHash} • Branch: {node.branchId.substring(0, 8)}...
        </div>
      </div>
    );
  }
};

export default InteractiveGitTreeVisualizer;